/** Helpers for web Payout Capture — aligned with PaySafe Mobile submit payload. */

export const VOUCHER_TICKET_MAX_LENGTH = 16

/** TITO / payout-card serial — alphanumeric, max 16 (matches mobile + API). */
export function sanitizeVoucherTicketNo(raw) {
  if (raw == null) return ''
  return String(raw).replace(/[^A-Za-z0-9]/g, '').slice(0, VOUCHER_TICKET_MAX_LENGTH)
}

/** Phone — digits and common separators only (matches mobile sanitizePatronPhone). */
export function sanitizePatronPhone(raw, maxLen = 24) {
  if (raw == null) return ''
  return String(raw).replace(/[^0-9+ ()-]/g, '').slice(0, maxLen)
}

/** AU EFT account number — digits only, max 9. */
export function sanitizeAustralianAccountNumber(raw) {
  if (raw == null) return ''
  return String(raw).replace(/\D/g, '').slice(0, 9)
}

/** Overseas account — digits and hyphen. */
export function sanitizeOverseasAccountNumber(raw, maxLen = 34) {
  if (raw == null) return ''
  return String(raw).replace(/[^0-9-]/g, '').slice(0, maxLen)
}

export const PAYMENT_CASH_DESK_CAP_AUD = 5000

export const JACKPOT_TYPES = ['Progressive', 'Cancel credit', 'Short pay', 'Other']

export const INITIAL_PAYOUT_CAPTURE_FORM = {
  patron_kind: 'member',
  member_id: '',
  visitor_id: '',
  first_name: '',
  last_name: '',
  member_name: '',
  venue: '',
  amount: '',
  payout_tier: 'PV1',
  occupation: '',
  /** Free text when occupation list selection is Other / Others */
  occupation_other: '',
  /** Remainder method (EFT/Cheque) when balance > 0 — same as mobile `paymentMethod`. */
  payment_method: 'EFT',
  cash_amount_paid: '',
  balance_to_pay: '',
  game_id: '',
  jackpot_type: 'Cancel credit',
  voucher_ticket_no: '',
  slip_number: '',
  slip_date: new Date().toISOString().slice(0, 10),
  patron_phone: '',
  patron_phone_refused: false,
  patron_address: '',
  document_option: '',
  document_type: '',
  document_number: '',
  document_card_number: '',
  date_of_birth: '',
  state_of_issue: '',
  country_of_issue: '',
  citizenship: '',
  /** 'single' | 'multiple' | '' */
  citizenship_mode: '',
  /** string[] when declaration used */
  citizenship_countries: [],
  expiry_date: '',
  kyc_mode: '',
  kyc_source: '',
  payout_mobile: '',
  eft_region: 'australian',
  eft_bank_name: '',
  eft_bsb: '',
  eft_account_number: '',
  eft_os_account_name: '',
  eft_os_bank_name: '',
  eft_os_bsb: '',
  eft_os_account_no: '',
  eft_os_swift: '',
  eft_os_branch_name: '',
  eft_os_branch_address: '',
  eft_os_contact_email: '',
  eft_os_contact_phone: '',
  eft_os_residential_address: '',
  bank_account_physically_verified: false,
  cheque_name_on: '',
  cheque_date: new Date().toISOString().slice(0, 10),
  notes: '',
}

/** Filter payment methods by venue payout settings (aligned with mobile `filterPaymentMethodsByRemote`). */
export function filterPaymentMethodsByPayoutSettings(methods, settings) {
  return (methods || []).filter((m) => {
    if (m === 'EFT') return settings?.eftEnabled !== false
    if (m === 'Cheque') return settings?.chequeEnabled !== false
    if (m === 'Cash') return settings?.showCashOption !== false
    return true
  })
}

/**
 * Tier cash method controls cash at desk only — not whether EFT/cheque can settle the remainder.
 * - CRT_OR_CASH: cash up to venue cap; remainder via EFT/cheque
 * - CASH_ONLY: full amount in cash when total ≤ cap; above cap, max cash + remainder via EFT/cheque
 * - NO_CASH: no cash at desk; full amount via EFT/cheque
 */
export function tierAllowsCash(cashDispensed, settings) {
  if (settings?.showCashOption === false) return false
  return cashDispensed !== 'NO_CASH'
}

/** True when CASH_ONLY tier and the full payout fits under the venue cash cap (all cash, no remainder). */
export function tierCashLockedToFullAmount(cashDispensed, totalAmount, settings) {
  if (cashDispensed !== 'CASH_ONLY') return false
  const total = parseAmount(totalAmount)
  if (!Number.isFinite(total) || total <= 0) return false
  return total <= effectiveMaxCashThisPayout(total, settings)
}

/** EFT / Cheque for remainder (or full amount when no cash) — all tiers; filtered by venue settings. */
export function balancePaymentMethods(settings) {
  return filterPaymentMethodsByPayoutSettings(['EFT', 'Cheque'], settings)
}

/** @deprecated Use `balancePaymentMethods(settings)` — cashDispensed does not gate remainder methods. */
export function balancePaymentMethodsForCashDispensed(_cashDispensed, settings) {
  return balancePaymentMethods(settings)
}

export function effectiveMaxCashThisPayout(totalAmount, settings) {
  const total = parseAmount(totalAmount)
  if (!Number.isFinite(total) || total <= 0) return 0
  const capRaw =
    settings?.cashLimit ??
    settings?.maxCashPerPayoutAUD ??
    settings?.maxCashPerPayout ??
    PAYMENT_CASH_DESK_CAP_AUD
  const cap = Math.max(0, parseAmount(capRaw) || PAYMENT_CASH_DESK_CAP_AUD)
  return Math.min(total, cap)
}

export function remainderIsEffectivelyZero(balance) {
  const n = parseAmount(balance)
  return !Number.isFinite(n) || n < 0.005
}

/** Default cash on payment step: min(total, venue cap) — mobile `applyDefaultCashOnPaymentStep`. */
export function defaultCashAndBalance(totalAmount, tierPolicy, settings) {
  const total = parseAmount(totalAmount)
  if (!Number.isFinite(total) || total <= 0) {
    return { cash: '', balance: '' }
  }
  const cashDispensed = tierPolicy?.cashDispensed
  if (!tierAllowsCash(cashDispensed, settings)) {
    return { cash: '0', balance: String(total) }
  }
  if (tierCashLockedToFullAmount(cashDispensed, total, settings)) {
    return { cash: String(total), balance: '0' }
  }
  const cash = effectiveMaxCashThisPayout(total, settings)
  return { cash: String(cash), balance: String(Math.max(0, total - cash)) }
}

/** Clamp cash and derive balance from total (mobile `syncBalanceFromCashVersusTotal`). */
export function syncBalanceFromCash(cashText, totalAmount, tierPolicy, settings) {
  const total = parseAmount(totalAmount)
  if (!Number.isFinite(total) || total <= 0) {
    return { cash_amount_paid: '', balance_to_pay: '' }
  }
  const cashDispensed = tierPolicy?.cashDispensed
  if (tierCashLockedToFullAmount(cashDispensed, total, settings)) {
    return { cash_amount_paid: String(total), balance_to_pay: '0' }
  }
  const maxCash = tierAllowsCash(cashDispensed, settings)
    ? effectiveMaxCashThisPayout(total, settings)
    : 0
  let cash = parseAmount(cashText)
  if (!Number.isFinite(cash)) cash = 0
  if (cash > maxCash) cash = maxCash
  if (cash > total) cash = total
  if (cash < 0) cash = 0
  const balance = Math.max(0, total - cash)
  return {
    cash_amount_paid: String(cash),
    balance_to_pay: String(balance),
  }
}

/** Derive cash from remainder edit (mobile `syncCashFromBalanceVersusTotal`). */
export function syncCashFromBalance(balanceText, totalAmount, tierPolicy, settings) {
  const total = parseAmount(totalAmount)
  if (!Number.isFinite(total) || total <= 0) {
    return { cash_amount_paid: '', balance_to_pay: '' }
  }
  const cap = tierAllowsCash(tierPolicy?.cashDispensed, settings)
    ? effectiveMaxCashThisPayout(total, settings)
    : 0
  let remainder = parseAmount(balanceText)
  if (!Number.isFinite(remainder)) remainder = 0
  if (remainder < 0) remainder = 0
  if (remainder > total) remainder = total
  let cash = total - remainder
  if (cash > cap) {
    cash = cap
    remainder = Math.max(0, total - cash)
  }
  if (cash < 0) {
    cash = 0
    remainder = total
  }
  return {
    cash_amount_paid: String(cash),
    balance_to_pay: String(remainder),
  }
}

export function formatPatronAddress(patron) {
  if (!patron || typeof patron !== 'object') return ''
  const parts = [
    patron.address1 || patron.address,
    patron.address2,
    [patron.suburb, patron.state, patron.postal_code].filter(Boolean).join(' '),
  ].filter((p) => p && String(p).trim())
  return parts.join(', ')
}

export function memberDisplayName(patron, fallback = '') {
  if (!patron) return fallback
  const name = [patron.firstname, patron.lastname].filter(Boolean).join(' ').trim()
  return name || fallback
}

/** Apply POST /api/payout/patron/resolve `data` onto form fields. */
export function applyPatronResolveToForm(prev, resolveData, occupationList) {
  if (!resolveData?.resolved || !resolveData.patron) return prev
  const p = resolveData.patron
  const name = memberDisplayName(p, prev.member_name)
  const address = formatPatronAddress(p) || prev.patron_address
  const dob = p.dob ? String(p.dob).slice(0, 10) : prev.date_of_birth
  const isVisitor = resolveData.patron_kind === 'visitor'
  const occPatch = applyOccupationFromProfile(prev, p.occupation, occupationList)
  return {
    ...prev,
    patron_kind: isVisitor ? 'visitor' : 'member',
    member_id: isVisitor ? '' : resolveData.member_id || prev.member_id,
    visitor_id:
      resolveData.visit_id || resolveData.visitor_id || p.entry_id || prev.visitor_id,
    first_name: p.firstname || prev.first_name,
    last_name: p.lastname || prev.last_name,
    member_name: name,
    patron_phone: p.mobile_phone || prev.patron_phone,
    patron_address: address,
    date_of_birth: dob,
    ...occPatch,
  }
}

export function parseAmount(value) {
  const n = parseFloat(String(value ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : NaN
}

/** List option is the catch-all Other row — staff enter free text instead. */
export function isOtherOccupationOption(value) {
  const normalized = String(value || '').trim().toLowerCase()
  return normalized === 'other' || normalized === 'others'
}

/** Occupation value stored on the payout record. */
export function resolvedOccupationValue(form) {
  const selection = String(form?.occupation || '').trim()
  if (isOtherOccupationOption(selection)) {
    return String(form?.occupation_other || '').trim()
  }
  return selection
}

/** Bank names for EFT picker (`settings.bankDirectory` from payout rules). */
export function bankPickerNamesFromDirectory(bankDirectory) {
  if (!bankDirectory || !Array.isArray(bankDirectory.banks)) return []
  return bankDirectory.banks
    .filter((b) => b.inPicker && String(b.name || '').trim())
    .sort(
      (a, b) =>
        (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) ||
        String(a.name).localeCompare(String(b.name))
    )
    .map((b) => String(b.name).trim())
}

/** Suggest bank name from BSB prefix map (AU). */
export function bankNameFromBsbPrefix(bankDirectory, bsbRaw) {
  if (!bankDirectory?.bsbPrefixes?.length) return null
  const digits = String(bsbRaw || '').replace(/\D/g, '')
  if (digits.length < 2) return null
  const banks = Array.isArray(bankDirectory.banks) ? bankDirectory.banks : []
  for (const row of bankDirectory.bsbPrefixes) {
    const prefix = String(row.prefix || '').replace(/\D/g, '')
    if (!prefix || !digits.startsWith(prefix)) continue
    const bankId = String(row.bankId || '').trim()
    const match = banks.find((b) => String(b.id) === bankId)
    if (match?.name) return String(match.name).trim()
  }
  return null
}

export function applyOccupationFromProfile(prev, profileOccupation, occupationList) {
  const raw = String(profileOccupation || '').trim()
  if (!raw) return { occupation: prev.occupation, occupation_other: prev.occupation_other }
  const list = Array.isArray(occupationList) ? occupationList.filter(Boolean) : []
  if (list.length === 0) {
    return { occupation: raw, occupation_other: '' }
  }
  if (list.includes(raw)) {
    return { occupation: raw, occupation_other: '' }
  }
  const otherOpt = list.find((o) => isOtherOccupationOption(o))
  if (otherOpt) {
    return { occupation: otherOpt, occupation_other: raw }
  }
  return { occupation: raw, occupation_other: '' }
}

export function validatePayoutCapture({ form, tierPolicy, patronResolve, settings }) {
  const amount = parseAmount(form.amount)
  if (!form.venue || !Number.isFinite(amount) || amount <= 0) {
    return 'Venue and amount are required'
  }
  if (tierPolicy && !tierPolicy.enabled) {
    return `${form.payout_tier} is disabled for this venue in the payout policy.`
  }
  if (tierPolicy?.occupationRequired && !resolvedOccupationValue(form)) {
    return `Occupation is required for ${form.payout_tier} at this venue (payout policy).`
  }
  if (patronResolve?.cdd_prohibited) {
    return 'Patron is CDD prohibited on watchlist — payout cannot be recorded.'
  }

  const cashDispensed = tierPolicy?.cashDispensed
  const allowsCash = tierAllowsCash(cashDispensed, settings)
  const balanceMethods = balancePaymentMethods(settings)
  const total = amount
  const cashPaid = parseAmount(form.cash_amount_paid)
  const balance = parseAmount(form.balance_to_pay)
  const maxCash = allowsCash ? effectiveMaxCashThisPayout(total, settings) : 0

  if (tierCashLockedToFullAmount(cashDispensed, total, settings)) {
    if (!Number.isFinite(cashPaid) || Math.abs(cashPaid - total) > 0.009) {
      return 'This tier is cash only — the full payout amount must be paid in cash at desk.'
    }
  } else if (allowsCash) {
    if (!Number.isFinite(cashPaid) || cashPaid < 0) {
      return 'Enter cash paid at desk.'
    }
    if (cashPaid > maxCash) {
      return `Cash at desk cannot exceed $${maxCash.toLocaleString()}.`
    }
    if (cashPaid > total) {
      return 'Cash paid cannot exceed the payout total.'
    }
  } else if (Number.isFinite(cashPaid) && cashPaid > 0.009) {
    return 'This tier does not allow cash — set cash paid to zero.'
  }

  const remainderZero = remainderIsEffectivelyZero(balance)
  if (!remainderZero && Math.abs((cashPaid || 0) + (balance || 0) - total) > 0.02) {
    return 'Cash paid plus remainder must equal the payout amount.'
  }

  let resolvedPaymentMethod
  if (remainderZero) {
    if (allowsCash) {
      resolvedPaymentMethod = 'Cash'
    } else {
      if (!balanceMethods.length) {
        return 'Venue rules do not allow a valid payment method for this amount.'
      }
      resolvedPaymentMethod = balanceMethods.includes(form.payment_method)
        ? form.payment_method
        : balanceMethods[0]
    }
  } else {
    if (!balanceMethods.length) {
      return 'There is a remainder after cash, but EFT and cheque are disabled for this venue. Enable at least one in payout settings.'
    }
    if (!balanceMethods.includes(form.payment_method)) {
      return 'Select EFT or cheque for the remainder.'
    }
    resolvedPaymentMethod = form.payment_method
  }

  const effectiveEft = resolvedPaymentMethod === 'EFT'
  const effectiveCheque = resolvedPaymentMethod === 'Cheque'

  if (effectiveEft) {
    if (form.eft_region === 'australian') {
      if (!String(form.eft_bsb || '').trim() || !String(form.eft_account_number || '').trim()) {
        return 'BSB and account number are required for EFT.'
      }
    } else if (
      !String(form.eft_os_account_name || '').trim() ||
      !String(form.eft_os_bank_name || '').trim() ||
      !String(form.eft_os_account_no || '').trim()
    ) {
      return 'Overseas EFT requires account name, bank name, and account number.'
    }
  }
  if (effectiveCheque && !String(form.cheque_name_on || '').trim()) {
    return 'Name on cheque is required.'
  }
  if (settings?.requirePatronPhone && !form.patron_phone_refused && !String(form.patron_phone || '').trim()) {
    return 'Patron phone is required (or mark as refused if allowed).'
  }
  return null
}

export function buildPayoutCapturePayload({
  form,
  venueObj,
  patronResolve,
  payoutSettings,
  cashierAttestation,
  patronAttestation,
  acknowledged,
  clientSubmissionId,
}) {
  const amount = parseAmount(form.amount)
  const venueName = venueObj?.name || form.venue
  const venueId = venueObj?.venue_id || venueObj?.id || form.venue
  const name =
    [form.first_name, form.last_name].filter((s) => String(s || '').trim()).join(' ').trim() ||
    String(form.member_name || '').trim() ||
    null

  const cashDispensed = venueObj?.policy?.[form.payout_tier]?.cashDispensed
  const allowsCash = tierAllowsCash(cashDispensed, payoutSettings)
  const balanceMethods = balancePaymentMethods(payoutSettings)
  const total = amount
  let cashPaid = parseAmount(form.cash_amount_paid)
  let balance = parseAmount(form.balance_to_pay)
  if (!Number.isFinite(cashPaid)) cashPaid = 0
  if (!Number.isFinite(balance)) balance = Math.max(0, total - cashPaid)

  let resolvedPaymentMethod
  if (remainderIsEffectivelyZero(balance)) {
    if (allowsCash) {
      resolvedPaymentMethod = 'Cash'
    } else {
      resolvedPaymentMethod = balanceMethods.includes(form.payment_method)
        ? form.payment_method
        : balanceMethods[0] || 'EFT'
    }
  } else {
    resolvedPaymentMethod = balanceMethods.includes(form.payment_method)
      ? form.payment_method
      : balanceMethods[0] || 'EFT'
  }

  const payload = {
    client_submission_id: clientSubmissionId,
    slip_date: form.slip_date || new Date().toISOString().slice(0, 10),
    slip_type: 'EGM',
    venue: venueName,
    venue_id: venueId,
    amount,
    payout_tier: form.payout_tier,
    payment_method: resolvedPaymentMethod,
    game_type: 'Slot Machine',
    game_id: form.game_id?.trim() || null,
    member_name: name,
    occupation: resolvedOccupationValue(form) || null,
    jackpot_type: form.jackpot_type,
    notes: form.notes?.trim() || null,
    attestation_version_ids: {
      ...(cashierAttestation?.id ? { cashier_confirm: cashierAttestation.id } : {}),
      ...(acknowledged && patronAttestation?.id ? { patron_receipt: patronAttestation.id } : {}),
    },
    capture_source: 'web',
  }

  if (form.patron_kind === 'visitor') {
    payload.visitor_id = form.visitor_id?.trim() || null
    payload.visit_id = form.visitor_id?.trim() || null
    payload.patron_kind = 'visitor'
  } else {
    payload.member_id = form.member_id?.trim() || null
    payload.patron_kind = 'member'
  }

  const phone = form.patron_phone?.trim()
  if (phone) {
    payload.patron_phone = phone
    payload.payout_mobile = phone
  }
  if (form.patron_phone_refused) payload.patron_phone_refused = true

  const address = form.patron_address?.trim()
  if (address) payload.patron_address = address

  if (Number.isFinite(cashPaid) && cashPaid >= 0) payload.cash_amount_paid = cashPaid
  if (Number.isFinite(balance) && balance >= 0) payload.balance_to_pay = balance

  const ticket = sanitizeVoucherTicketNo(form.voucher_ticket_no?.trim())
  if (ticket) payload.voucher_ticket_no = ticket
  const slipRef = form.slip_number?.trim()
  if (slipRef) payload.slip_number = slipRef

  if (form.bank_account_physically_verified) {
    payload.bank_account_physically_verified = true
  }

  if (resolvedPaymentMethod === 'EFT') {
    const eft =
      form.eft_region === 'overseas'
        ? {
            region: 'overseas',
            account_name: form.eft_os_account_name?.trim() || '',
            bank_name: form.eft_os_bank_name?.trim() || '',
            bsb: form.eft_os_bsb?.trim() || '',
            account_number: form.eft_os_account_no?.trim() || '',
            swift_bic: form.eft_os_swift?.trim() || '',
            branch_name: form.eft_os_branch_name?.trim() || '',
            branch_address: form.eft_os_branch_address?.trim() || '',
            contact_email: form.eft_os_contact_email?.trim() || '',
            contact_phone: form.eft_os_contact_phone?.trim() || '',
            residential_address: form.eft_os_residential_address?.trim() || '',
          }
        : {
            region: 'australian',
            bank_name: form.eft_bank_name?.trim() || '',
            bsb: form.eft_bsb?.trim() || '',
            account_number: form.eft_account_number?.trim() || '',
          }
    payload.eft_details = eft
  }

  if (resolvedPaymentMethod === 'Cheque') {
    payload.cheque_details = {
      name_on_cheque: form.cheque_name_on?.trim() || '',
      cheque_date: form.cheque_date || new Date().toISOString().slice(0, 10),
    }
  }

  const docNo = form.document_number?.trim()
  if (docNo) payload.document_number = docNo
  const cardNo = form.document_card_number?.trim()
  if (cardNo) payload.document_card_number = cardNo
  const dob = form.date_of_birth?.trim()
  if (dob) payload.date_of_birth = dob
  const docOpt = form.document_option?.trim()
  if (docOpt) payload.document_option = docOpt
  const docType = form.document_type?.trim()
  if (docType) payload.document_type = docType
  const state = form.state_of_issue?.trim()
  if (state) payload.state_of_issue = state
  const countryOfIssue = form.country_of_issue?.trim()
  if (countryOfIssue) {
    payload.country_of_issue = countryOfIssue
    payload.country_code = countryOfIssue
  }
  const citizenship = form.citizenship?.trim()
  if (citizenship) payload.citizenship = citizenship
  const citizenshipMode = String(form.citizenship_mode || '').trim().toLowerCase()
  if (citizenshipMode === 'single' || citizenshipMode === 'multiple') {
    payload.citizenship_mode = citizenshipMode
  }
  if (Array.isArray(form.citizenship_countries) && form.citizenship_countries.length) {
    payload.citizenship_countries = form.citizenship_countries
      .map((c) => String(c || '').trim())
      .filter(Boolean)
  }
  const expiry = form.expiry_date?.trim()
  if (expiry) payload.expiry_date = expiry
  if (form.kyc_mode) payload.kyc_mode = form.kyc_mode
  if (form.kyc_source) payload.kyc_source = form.kyc_source

  if (patronResolve && typeof patronResolve === 'object') {
    payload.patron_resolve = patronResolve
    const mpId = patronResolve.master_profile_id != null ? String(patronResolve.master_profile_id).trim() : ''
    if (mpId) payload.master_profile_id = mpId
  }

  return payload
}
